/**
 * Gemeinsame Scroll-/Zähl-Effekte der Feature-Detailseiten (portiert aus den
 * gelieferten Standalone-Seiten). Host-gescoped, damit die generischen Klassen
 * nicht mit dem Rest der Seite kollidieren. Nur im Browser aufrufen
 * (ngAfterViewInit) – gibt die IntersectionObserver für die Cleanup-Phase zurück.
 */
export function initFeatureFx(host: HTMLElement): IntersectionObserver[] {
  const observers: IntersectionObserver[] = [];
  host.classList.add('js');
  const reduce =
    typeof matchMedia !== 'undefined' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Scroll-Reveal (.reveal + .frow)
  const reveals = Array.from(host.querySelectorAll<HTMLElement>('.reveal, .frow'));
  if (reduce || typeof IntersectionObserver === 'undefined') {
    reveals.forEach((r) => r.classList.add('reveal-in'));
  } else {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('reveal-in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    reveals.forEach((r) => io.observe(r));
    observers.push(io);
  }

  // Zählende Kennzahlen im .band
  const band = host.querySelector('.band');
  if (band) {
    const stats = () => host.querySelectorAll<HTMLElement>('.stat .n[data-count]');
    // SSR / kein rAF: Endwerte direkt setzen, ohne Animation. requestAnimationFrame
    // existiert beim Server-Prerender nicht – sonst ReferenceError in ngAfterViewInit.
    if (typeof requestAnimationFrame === 'undefined' || typeof IntersectionObserver === 'undefined') {
      stats().forEach((el) => {
        el.textContent =
          (parseFloat(el.getAttribute('data-count') || '0') || 0) +
          (el.getAttribute('data-suffix') || '');
      });
    } else {
      const run = () => {
        stats().forEach((el) => {
          const target = parseFloat(el.getAttribute('data-count') || '0') || 0;
          const suf = el.getAttribute('data-suffix') || '';
          const dur = 1400;
          let start = 0;
          const step = (ts: number) => {
            if (!start) start = ts;
            const p = Math.min((ts - start) / dur, 1);
            el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3))) + suf;
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        });
      };
      const io2 = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              run();
              io2.disconnect();
            }
          });
        },
        { threshold: 0.4 }
      );
      io2.observe(band);
      observers.push(io2);
    }
  }
  // Sanftes Scrollen für seiteninterne Anker (#so, #faq, #blick …). Ersetzt die
  // Router-Fragment-Navigation, die sonst nicht scrollt. Es werden nur Anker
  // abgefangen, deren Ziel innerhalb dieser Seite (host) liegt – z. B. #contact
  // (Footer) bleibt unangetastet.
  host.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((a) => {
    a.addEventListener('click', (e) => {
      const id = (a.getAttribute('href') || '').slice(1);
      if (!id) return;
      let target: Element | null = null;
      try {
        target = host.querySelector('#' + (window.CSS?.escape ? CSS.escape(id) : id));
      } catch {
        target = null;
      }
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  return observers;
}
